import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

export default function VastuCompass() {
  const mountRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!mountRef.current) return;
    const w = mountRef.current.clientWidth;
    const h = mountRef.current.clientHeight;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, w / h, 0.1, 100);
    camera.position.set(0, 0, 5);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(w, h);
    renderer.setPixelRatio(window.devicePixelRatio);
    mountRef.current.appendChild(renderer.domElement);

    // Outer ring
    const ringGeo = new THREE.TorusGeometry(2, 0.04, 16, 100);
    const goldMat = new THREE.MeshBasicMaterial({ color: 0xE8871A });
    scene.add(new THREE.Mesh(ringGeo, goldMat));

    const innerRing = new THREE.Mesh(new THREE.TorusGeometry(1.5, 0.02, 16, 100), goldMat);
    scene.add(innerRing);

    // 8 direction spokes
    const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    const colors = [0xE8871A, 0xC9547A, 0xE8871A, 0xC9547A, 0xE8871A, 0xC9547A, 0xE8871A, 0xC9547A];
    directions.forEach((_, i) => {
      const angle = (i / 8) * Math.PI * 2;
      const spokeGeo = new THREE.CylinderGeometry(0.015, 0.015, 0.5, 8);
      const spoke = new THREE.Mesh(spokeGeo, new THREE.MeshBasicMaterial({ color: colors[i] }));
      spoke.position.set(Math.sin(angle) * 1.75, Math.cos(angle) * 1.75, 0);
      spoke.rotation.z = -angle;
      scene.add(spoke);
    });

    // Center mandala
    const centerGeo = new THREE.CircleGeometry(0.3, 8);
    const centerMat = new THREE.MeshBasicMaterial({ color: 0xE8871A, wireframe: true });
    scene.add(new THREE.Mesh(centerGeo, centerMat));

    // Triangles (Star of David style)
    const triPoints1 = [
      new THREE.Vector3(0, 1.2, 0),
      new THREE.Vector3(-1.04, -0.6, 0),
      new THREE.Vector3(1.04, -0.6, 0),
    ];
    const triPoints2 = [
      new THREE.Vector3(0, -1.2, 0),
      new THREE.Vector3(-1.04, 0.6, 0),
      new THREE.Vector3(1.04, 0.6, 0),
    ];
    [triPoints1, triPoints2].forEach((pts, idx) => {
      const geo = new THREE.BufferGeometry().setFromPoints([...pts, pts[0]]);
      scene.add(new THREE.Line(geo, new THREE.LineBasicMaterial({
        color: idx === 0 ? 0xE8871A : 0xC9547A, transparent: true, opacity: 0.7
      })));
    });

    // Particles
    const particleGeo = new THREE.BufferGeometry();
    const positions = new Float32Array(300);
    for (let i = 0; i < 300; i++) {
      positions[i] = (Math.random() - 0.5) * 6;
    }
    particleGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    scene.add(new THREE.Points(particleGeo, new THREE.PointsMaterial({ color: 0xE8871A, size: 0.02, transparent: true, opacity: 0.4 })));

    let frame: number;
    const animate = () => {
      frame = requestAnimationFrame(animate);
      scene.rotation.z += 0.003;
      renderer.render(scene, camera);
    };
    animate();

    const handleResize = () => {
      if (!mountRef.current) return;
      const nw = mountRef.current.clientWidth;
      const nh = mountRef.current.clientHeight;
      camera.aspect = nw / nh;
      camera.updateProjectionMatrix();
      renderer.setSize(nw, nh);
    };
    window.addEventListener('resize', handleResize);

    const mountNode = mountRef.current;
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', handleResize);
      renderer.dispose();
      if (mountNode && renderer.domElement.parentNode === mountNode) {
        mountNode.removeChild(renderer.domElement);
      }
    };
  }, []);

  return <div ref={mountRef} style={{ width: '100%', height: '100%' }} />;
}
